import { useState, useEffect, useMemo, type RefObject } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import moment from 'moment-timezone';
import { KModal } from '@/ui-library/components';
import { DateSinglePicker } from '@/design-system/date-picker';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { KText } from '@/ui-library/components/text';
import { SCHEDULE_AI_COPY } from '../../constants';
import { TIMEZONE_OPTIONS, TIME_OPTIONS_15MIN } from '../options';

const COPY = SCHEDULE_AI_COPY;

function isEntireDayPastInZone(dayDate, timezoneKey) {
  const ymd = moment(dayDate).format('YYYY-MM-DD');
  const endOfDay = moment.tz(
    `${ymd} 23:59:59`,
    'YYYY-MM-DD HH:mm:ss',
    timezoneKey
  );
  return endOfDay.isBefore(moment());
}

/**
 * Scheduled instant for modal date + HH:mm in candidate TZ — must match `combined` / Set schedule validation.
 */
function scheduledMomentForModal(selectedDate, hhmm, timezoneKey) {
  return moment.tz(
    `${moment(selectedDate).format('YYYY-MM-DD')} ${hhmm}`,
    timezoneKey
  );
}

/** Same rule as Set schedule: only options strictly after `moment().subtract(1, 'minute')`. */
function getFutureTimeOptions(selectedDate, timezoneKey) {
  const cutoff = moment().subtract(1, 'minute');
  return TIME_OPTIONS_15MIN.filter((opt) =>
    scheduledMomentForModal(selectedDate, opt.value, timezoneKey).isAfter(
      cutoff
    )
  );
}

/** Custom date/time picker modal. Candidate timezone is read-only (defaultTimezoneKey). Value: { date, time, timezoneKey } with timezoneKey = candidate's. */
export const CustomDateModal = ({
  isOpen,
  onClose,
  initialValue,
  onConfirm,
  defaultTimezoneKey = 'America/Los_Angeles',
  /** When set (wrap timing UI in a Box with tabIndex={-1} + this ref), focus returns here after close — fixes nested drawer + modal leaving react-select blocked. */
  finalFocusRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialValue?: { date?: Date; time?: string; timezoneKey?: string } | null;
  onConfirm: (_v: { date: Date; time: string; timezoneKey: string }) => void;
  defaultTimezoneKey?: string;
  finalFocusRef?: RefObject<HTMLElement | null>;
}) => {
  const [date, setDate] = useState(() => initialValue?.date || new Date());
  const [time, setTime] = useState(() => initialValue?.time || '09:00');

  const timeOptions = useMemo(
    () => getFutureTimeOptions(date, defaultTimezoneKey),
    [date, defaultTimezoneKey]
  );

  useEffect(() => {
    if (!isOpen) return;
    const nextDate = initialValue?.date || new Date();
    const nextTime = initialValue?.time || '09:00';
    const id = setTimeout(() => {
      setDate(nextDate);
      setTime(nextTime);
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen, initialValue?.date, initialValue?.time]);

  // Derive effective time: if current selection isn't in the (filtered) list, use first available
  const effectiveTime =
    timeOptions.length > 0 && !timeOptions.some((o) => o.value === time)
      ? timeOptions[0].value
      : time;

  const candidateTzLabel =
    TIMEZONE_OPTIONS.find((o) => o.value === defaultTimezoneKey)?.label ||
    defaultTimezoneKey;

  const handleSetSchedule = () => {
    onConfirm({ date, time: effectiveTime, timezoneKey: defaultTimezoneKey });
    onClose();
  };

  const cutoff = moment().subtract(1, 'minute');
  const combined = scheduledMomentForModal(
    date,
    effectiveTime,
    defaultTimezoneKey
  );
  const isFuture = combined.isAfter(cutoff);

  return (
    <KModal
      isOpen={isOpen}
      onClose={onClose}
      title={COPY.timingLabel}
      footer={true}
      enableCancelBtn={true}
      ctaAction={handleSetSchedule}
      ctaLabel={COPY.setSchedule}
      ctaButtonProps={{ isDisabled: !isFuture }}
      size="sm"
      modalFooterProps={{ justifyContent: 'flex-end', gap: '12px' }}
      modalBodyProps={{ p: '16px 24px' }}
      finalFocusRef={finalFocusRef}
      returnFocusOnClose={Boolean(finalFocusRef)}
      trapFocus={false}
      blockScrollOnMount={false}
      useInert={false}
    >
      <Flex flexDir="column" gap="20px">
        <Box>
          <DateSinglePicker
            isStatic
            value={date}
            onChange={setDate}
            disabledDay={(d) => isEntireDayPastInZone(d, defaultTimezoneKey)}
          />
        </Box>
        <Box>
          <KText size="sm-semi-bold" color="secondary-900">
            {COPY.time}
          </KText>
          <Box mt="8px">
            <KSelect
              options={timeOptions}
              value={
                timeOptions.find((o) => o.value === effectiveTime) ||
                timeOptions[0] ||
                null
              }
              onChange={(opt) => opt && setTime(opt.value)}
              placeholder="Select time"
            />
          </Box>
        </Box>
        <Box>
          <KText size="sm-semi-bold" color="secondary-900">
            {COPY.candidateTimezone}
          </KText>
          <KText fontSize="14px" color="#413B74" mt="8px">
            {candidateTzLabel}
          </KText>
        </Box>
      </Flex>
    </KModal>
  );
};
